import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import { ISavedLandmarks } from 'src/features/annotation/domain/types';
import {
	FabricManager,
	IAnnotationData,
	Tool,
} from 'src/features/annotation/service/FabricManager';
import { getAnnotationLandmarks } from 'src/features/annotation/store/annotationLandmarkThunks';
import { IAnnotationLabelShortInfo } from 'src/features/annotation_label';
import { IStudyAnnotation } from 'src/features/study';
import { AppDispatch } from 'src/store';

import { AnnotationPanel } from './AnnotationPanel';
import { AnnotationToolbar } from './AnnotationToolbar';

type FabricImageAnnotatorProps = {
	imageURL: string;
	currentAnnotation: IStudyAnnotation | null;
	assetAnnotations?: IStudyAnnotation[];
	annotationLabels: IAnnotationLabelShortInfo[];
	onCurrentAnnotationUpdate: (data: IStudyAnnotation) => void;
	disabled: boolean;
};

export const FabricImageAnnotator: React.FC<FabricImageAnnotatorProps> = ({
	imageURL,
	currentAnnotation,
	onCurrentAnnotationUpdate,
	disabled = false,
	assetAnnotations,
	annotationLabels,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const fabricManager = useRef<FabricManager | null>(null);

	const [tool, setTool] = useState<Tool>('select');
	const [landmarks, setLandmarks] = useState<IAnnotationData[]>([]);
	const [savedLandmarks, setSavedLandmarks] = useState<ISavedLandmarks[]>([]);
	const [rightPanelOpen, setRightPanelOpen] = useState(!disabled);
	const [triggerResize, setTriggerResize] = useState(false);
	const [isFabricInitialized, setIsFabricInitialized] = useState(false);

	const toggleRightPanel = () => {
		if (!containerRef.current || !fabricManager.current) return;

		const currentContainerWidth =
			containerRef.current.getBoundingClientRect().width;
		let newCanvasWidth: number;

		// Calculate the future width of the canvas container
		if (rightPanelOpen) {
			// If panel is currently open, we are closing it
			// Panel will shrink from 300px to 40px, so canvas gets bigger
			newCanvasWidth = currentContainerWidth + 260; // 300 - 40 = 260

			setTimeout(() => {
				if (!containerRef.current || !fabricManager.current) return;

				fabricManager.current.resizeCanvas(
					newCanvasWidth,
					containerRef.current.getBoundingClientRect().height,
				);
			}, 500);
		} else {
			// If panel is currently closed, we are opening it
			// Panel will expand from 40px to 300px, so canvas shrinks
			newCanvasWidth = currentContainerWidth - 260;

			fabricManager.current.resizeCanvas(
				newCanvasWidth,
				containerRef.current.getBoundingClientRect().height,
			);
		}

		setRightPanelOpen(!rightPanelOpen);
	};

	useEffect(() => {
		if (!canvasRef.current || !containerRef.current) return;

		const manager = new FabricManager(canvasRef.current);
		fabricManager.current = manager;
		manager.setTool(tool);

		manager.onAnnotationCreated((data) => {
			if (!fabricManager.current) return;

			setLandmarks((prev) => {
				const updated = prev.map((a) => {
					if (!a.locked) {
						fabricManager.current?.lockAnnotation(a.id);
						return { ...a, locked: true };
					}
					return a;
				});

				return [...updated, data];
			});
		});

		manager.onAnnotationRemoved((id) => {
			setLandmarks((prev) => prev.filter((a) => a.id !== id));
		});

		manager.onAnnotationsUpdated((data) => {
			setLandmarks(data);
		});

		manager.onToolChange((tool) => {
			setTool(tool);
		});

		(async () => {
			await manager.setBackgroundImage(imageURL);
			setTriggerResize(true);
			setIsFabricInitialized(true);
		})();

		return () => {
			setIsFabricInitialized(false);
			setTriggerResize(false);
			fabricManager.current?.dispose();
			fabricManager.current = null;
		};
	}, [imageURL]);

	useEffect(() => {
		if (!triggerResize || !containerRef.current || !fabricManager.current)
			return;

		const { width, height } = containerRef.current.getBoundingClientRect();
		fabricManager.current.resizeCanvas(width, height);
		setTriggerResize(false);
	}, [triggerResize]);

	useEffect(() => {
		fabricManager.current?.setTool(tool);
	}, [tool]);

	useEffect(() => {
		if (!isFabricInitialized) return;

		const annotationsToFetch: IStudyAnnotation[] = [
			...(assetAnnotations || []),
			...(currentAnnotation ? [currentAnnotation] : []),
		];

		if (annotationsToFetch.length === 0) return;

		const allSaved: ISavedLandmarks[] = [];
		const allFabricData: IAnnotationData[] = [];

		const fetchAllLandmarks = async () => {
			for (const annotation of annotationsToFetch) {
				await dispatch(
					getAnnotationLandmarks({
						annotationId: annotation.id,
						siteSlug: siteSlug!,
						onSuccess: (response) => {
							const saved: ISavedLandmarks[] = response
								.map((landmark) => {
									try {
										return {
											...landmark,
											meta_data: JSON.parse(
												landmark.meta_data,
											),
										};
									} catch (err) {
										console.error(
											`Failed to parse meta_data for landmark ${landmark.id}`,
											err,
										);
										return null;
									}
								})
								.filter(Boolean) as ISavedLandmarks[];

							allSaved.push(...saved);
							allFabricData.push(
								...saved.map((l) =>
									structuredClone(l.meta_data),
								),
							);
						},
					}),
				);
			}

			fabricManager.current?.importAnnotations(allFabricData);
		};

		const hydrateStates = async () => {
			await fetchAllLandmarks();
			setSavedLandmarks(allSaved);
			setLandmarks(allFabricData.map((a) => structuredClone(a)));
		};

		hydrateStates();
	}, [isFabricInitialized, currentAnnotation, assetAnnotations]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				maxHeight: '100%',
			}}
		>
			{/* Toolbar */}
			<AnnotationToolbar
				tool={tool}
				setTool={setTool}
				currentAnnotation={currentAnnotation}
				onCurrentAnnotationCancel={onCurrentAnnotationUpdate}
				disabled={disabled}
			/>

			{/* Main Viewer */}
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					minWidth: 0,
					overflow: 'hidden',
				}}
			>
				{/* Canvas Container */}
				<Box
					ref={containerRef}
					sx={{
						flex: 1,
						minWidth: 0,
						display: 'flex',
						position: 'relative',
						backgroundColor: 'gray',
						borderRadius: '8px 0px 0px 8px',
					}}
				>
					<canvas
						ref={canvasRef}
						style={{
							display: 'block',
							width: '100%',
							height: '100%',
						}}
					/>
				</Box>
				{/* Right Panel */}
				<AnnotationPanel
					rightPanelOpen={rightPanelOpen}
					toggleRightPanel={toggleRightPanel}
					disabled={disabled}
					currentAnnotation={currentAnnotation}
					landmarks={landmarks}
					savedLandmarks={savedLandmarks}
					setLandmarks={setLandmarks}
					setSavedLandmarks={setSavedLandmarks}
					fabricManager={fabricManager.current}
					annotationLabels={annotationLabels}
					onCurrentAnnotationUpdate={onCurrentAnnotationUpdate}
				/>
			</Box>
		</Box>
	);
};
